import React, { useState, useEffect } from 'react'
import { useSelector, shallowEqual, useDispatch } from 'react-redux'
import { ContextButton } from '@tecuity/granite-ui/ContextButton'
import { Detail } from '@tecuity/granite-ui/Detail'
import { ConfirmDialog } from 'components/Modal/Modal'
import * as api from '../../../requests'
import format from 'date-fns/format'
import PencilIcon from '@tecuity/sos-icons/react/pencil'
import ArchiveIcon from '@tecuity/sos-icons/react/archive'
import CheckBoxIcon from '@tecuity/sos-icons/react/checkbox'
import CrossIcon from '@tecuity/sos-icons/react/cross-heavy'
// import CaretDownIcon from '@tecuity/sos-icons/react/caret-down'
// import CaretUpIcon from '@tecuity/sos-icons/react/caret-up'
import './WorkViewItemNotes.scss'
import { Spinner } from '../../Loaders/Loaders'
import { SET_WORK_VIEW_ITEM_NOTES } from '../Workview2Reducer'

const WorkViewItemNotes = (props) => {
  const dispatch = useDispatch()
  const { appUserRights } = useSelector((state) => state.auth, shallowEqual)
  const { workViewItemNotes = [], selectedRow } = useSelector(
    (state) => state.workview2,
    shallowEqual
  )
  const [noteText, setNoteText] = useState('')
  const [editingNote, setEditingNote] = useState({})
  const [deletingNoteId, setDeletingNoteId] = useState(null)
  const [confirmDialogIsOpen, setConfirmDialogIsOpen] = useState(false)
  const [loading, setLoading] = useState(false)
  const { updateRow } = props

  useEffect(() => {
    getNotes()
    return () => {
      dispatch({
        type: SET_WORK_VIEW_ITEM_NOTES,
        notes: [],
      })
    }
  }, [selectedRow.WorkViewItemId])

  const checkToUpdateRow = (newResults) => {
    if (selectedRow.HasNotes && newResults.length === 0) updateRow()
    else if (!selectedRow.HasNotes && newResults.length > 0) updateRow()
  }

  const getNotes = () => {
    if (appUserRights.includes('ViewWorkViewNotes')) {
      // get notes for selected work view item.
      setLoading(true)
      api.GET_WORK_VIEW_ITEM_NOTES_BY_WORK_VIEW_ITEM_ID(
        selectedRow.WorkViewItemId,
        (res) => {
          dispatch({
            type: SET_WORK_VIEW_ITEM_NOTES,
            notes: res.sort((a, b) => {
              const eh = new Date(a.CreatedDate).getTime()
              const bee = new Date(b.CreatedDate).getTime()
              return eh < bee ? 1 : -1
            }),
          })
          checkToUpdateRow(res)
          setLoading(false)
        },
        (err) => {
          console.error(err)
          setLoading(false)
        }
      )
    }
  }

  const saveNote = (text) => {
    if (appUserRights.includes('CreateWorkViewNotes')) {
      const payload = {
        // ...defaultDTO,
        NoteText: text,
        WorkViewItemId: selectedRow.WorkViewItemId,
      }
      setLoading(true)
      api.ADD_WORK_VIEW_ITEM_NOTE(
        payload,
        (res) => {
          getNotes()
        },
        (err) => {
          console.error(err)
          setLoading(false)
        }
      )
    }
  }

  const editNote = () => {
    if (appUserRights.includes('EditWorkViewNotes')) {
      // update existing note
      setLoading(true)
      api.EDIT_WORK_VIEW_ITEM_NOTE(
        editingNote,
        (res) => {
          getNotes()
        },
        (err) => {
          console.error(err)
          setLoading(false)
        }
      )
    }
  }

  const deleteNote = (noteId) => {
    if (appUserRights.includes('DeleteWorkViewNotes')) {
      // delete note by id
      setLoading(true)
      api.DELETE_WORK_VIEW_ITEM_NOTE_BY_NOTE_ID(
        deletingNoteId,
        (res) => {
          getNotes()
        },
        (err) => {
          console.error(err)
          setLoading(false)
        }
      )
    }
  }

  const toggleConfirmDialog = (note = null) => {
    if (!!note) {
      setDeletingNoteId(note.WorkViewItemNoteId)
    } else {
      setDeletingNoteId(null)
    }
    setConfirmDialogIsOpen(!confirmDialogIsOpen)
  }

  return (
    <div>
      {appUserRights.includes('CreateWorkViewNotes') && (
        <div style={{ display: 'flex', flexDirection: 'row', padding: 10 }}>
          <div className={'note-text-area'} style={{ flexGrow: '4' }}>
            <label htmlFor={'noteTextArea'}>Note</label>
            <textarea
              name={'noteTextArea'}
              value={noteText}
              onChange={(e) => setNoteText(e.target.value)}
              rows={3}
            />
          </div>
          <ContextButton
            disabled={noteText === '' ? true : undefined}
            customCss={`margin-top: 16px; margin-left: 5px; text-transform: none !important; height: 46px;`}
            onClick={() => {
              saveNote(noteText)
              setNoteText('')
            }}
          >
            Add Note
          </ContextButton>
        </div>
      )}
      {loading && (
        <div className="modal-spinner-wrapper">
          <Spinner themeColor="dark-neutral" size="small" />
        </div>
      )}
      {workViewItemNotes && workViewItemNotes.length > 0 ? (
        <div className="note-word-break">
          {(workViewItemNotes || []).map((note, i) => {
            const parts = note.NoteText.split('\n')
            return (
              <div key={i}>
                <Detail
                  label={`Note: ${note.WorkViewItemNoteId} - ${note.LastModifiedByName} ${format(
                    new Date(note.LastModifiedDate),
                    'MM/dd/yyyy hh:mm a'
                  )}${
                    new Date(note.LastModifiedDate).getTime() !==
                    new Date(note.CreatedDate).getTime()
                      ? ' (edited)'
                      : ''
                  }`}
                  fontSize="small"
                  customCss={`margin-bottom: 4px;`}
                >
                  {!editingNote || note.WorkViewItemNoteId !== editingNote.WorkViewItemNoteId ? (
                    <div
                      style={{
                        display: 'flex',
                        flexDirection: 'row',
                        width: '100%',
                        justifyContent: 'space-between',
                      }}
                    >
                      <div>
                        {(parts || []).map((part, j) => {
                          return (
                            <React.Fragment key={'' + note.WorkViewItemNoteId + j}>
                              {part}
                              {j !== parts.length - 1 ? <br /> : null}
                            </React.Fragment>
                          )
                        })}
                      </div>

                      <div>
                        {appUserRights.includes('EditWorkViewNotes') && (
                          <button
                            className="btn btn-primary btn-text"
                            onClick={() => {
                              setEditingNote(note)
                            }}
                            title={`Edit Note: ${note.WorkViewItemNoteId}`}
                            alt={`Edit Note: ${note.WorkViewItemNoteId}`}
                          >
                            <PencilIcon />
                          </button>
                        )}
                        {appUserRights.includes('DeleteWorkViewNotes') && (
                          <button
                            className="btn btn-primary btn-text btn-danger"
                            onClick={() => {
                              toggleConfirmDialog(note)
                            }}
                            title={`Delete Note: ${note.WorkViewItemNoteId}`}
                            alt={`Delete Note: ${note.WorkViewItemNoteId}`}
                          >
                            <ArchiveIcon />
                          </button>
                        )}
                      </div>
                    </div>
                  ) : (
                    <div
                      className="edit-note note-text-area"
                      style={{ display: 'flex', flexDirection: 'row', width: '100%' }}
                    >
                      <div style={{ flex: 1 }}>
                        <label for="editNoteTextArea">Edit Note</label>
                        <textarea
                          aria-labelledby="editNoteTextArea"
                          name={'editNoteTextArea'}
                          value={editingNote.NoteText}
                          onChange={(e) =>
                            setEditingNote({ ...editingNote, NoteText: e.target.value })
                          }
                          rows={2}
                        />
                      </div>
                      <div style={{ display: 'flex', justifyContent: 'end' }}>
                        <button
                          className="btn btn-primary btn-text"
                          onClick={() => {
                            editNote(editingNote)
                            setEditingNote(null)
                          }}
                          alt={`Save Note: ${note.WorkViewItemNoteId}`}
                          title={`Save Note: ${note.WorkViewItemNoteId}`}
                        >
                          <CheckBoxIcon />
                        </button>
                        <button
                          className="btn btn-primary btn-text"
                          onClick={() => {
                            setEditingNote(null)
                          }}
                          title={`Cancel Editing Note: ${note.WorkViewItemNoteId}`}
                          alt={`Cancel Editing Note: ${note.WorkViewItemNoteId}`}
                        >
                          <CrossIcon />
                        </button>
                      </div>
                    </div>
                  )}
                </Detail>
              </div>
            )
          })}
        </div>
      ) : !loading ? (
        <div className="empty-notes-message">No Notes Found</div>
      ) : null}
      {confirmDialogIsOpen && (
        <ConfirmDialog
          closeModal={toggleConfirmDialog}
          onModalClosed={() => {}}
          message="Are you sure you want to delete this note?"
          buttons={[
            { label: 'No', onClick: () => {}, className: 'btn-default' },
            {
              label: 'Yes',
              onClick: () => {
                deleteNote(deletingNoteId)
              },
              className: 'btn-danger',
            },
          ]}
          modalStyle={{ borderRadius: 3 }}
        />
      )}
    </div>
  )
}

export default WorkViewItemNotes

// const defaultDTO = {
//   WorkViewItemNoteId: null,
//   WorkViewItemId: 0,
//   NoteText: '',
//   CreatedDate: null,
//   LastModifiedDate: null,
//   CreatedByUserId: null,
//   CreatedByName: null,
//   LastModifiedByUserId: null,
//   LastModifiedByName: null,
// }
